/**
 * Skin registry: Velora Modern is bundled; every other mode is a lazy
 * chunk (definition, tokens, terms, keymap, chrome), fetched the first time
 * someone uses it, so modes nobody selected cost nothing. Owner: WP0-SHELL.
 */
import type { SkinId } from '@velora/contracts/skins';
import type { SkinDefinition } from './types.js';
import { skin as velora } from './velora.js';

type Loader = () => Promise<{ skin: SkinDefinition }>;

const LOADERS: Record<SkinId, Loader> = {
  velora: () => Promise.resolve({ skin: velora }),
  dentrix: () => import('./dentrix.js'),
  eaglesoft: () => import('./eaglesoft.js'),
  opendental: () => import('./opendental.js'),
};

const cache = new Map<SkinId, SkinDefinition>([['velora', velora]]);
const pending = new Map<SkinId, Promise<SkinDefinition>>();

export const VELORA_SKIN = velora;

/** The definition if it is already loaded (synchronous, for first paint). */
export function loadedSkin(id: SkinId): SkinDefinition | undefined {
  return cache.get(id);
}

export function loadSkin(id: SkinId): Promise<SkinDefinition> {
  const hit = cache.get(id);
  if (hit) return Promise.resolve(hit);
  let p = pending.get(id);
  if (!p) {
    p = LOADERS[id]().then(
      ({ skin }) => {
        if (skin.id !== id) throw new Error(`skins/${id}.ts exports skin "${skin.id}"`);
        if (skin.layout === 'classic' && !skin.chrome) {
          throw new Error(`skin "${id}" uses the classic layout but has no chrome`);
        }
        cache.set(id, skin);
        pending.delete(id);
        return skin;
      },
      (err: unknown) => {
        pending.delete(id);
        throw err;
      },
    );
    pending.set(id, p);
  }
  return p;
}

/** Test hook: forget loaded skins (Velora stays). */
export function resetSkinCache(): void {
  for (const id of [...cache.keys()]) if (id !== 'velora') cache.delete(id);
  pending.clear();
}
